import {
    FieldStructure,
    InputDropdownFieldStructure,
    CustomFieldStructure,
} from '@mercell/form-react';
import {
    FormDefinitionItem,
    FormOptionItem,
} from '../../types/generated/formDefinitionItem';
import { getRulesSetup } from '../getRulesSetup';
import * as yup from 'yup';
import { TFunction } from 'react-i18next';
import i18next from 'i18next';
import { formatNumberForLocale } from './numericFieldUtils';
import { createNumericInputDropdownRender } from './numericInputDropdown';

export const mapInputWithDropdownField = (
    formDefinition: FormDefinitionItem,
    genericFieldStructure: FieldStructure,
    t: TFunction<string, unknown>,
    language?: string,
    enableNumericFormatting?: boolean
): InputDropdownFieldStructure | CustomFieldStructure => {
    const locale = language ?? i18next.language ?? 'en-GB';
    const ValueField = formDefinition?.fields?.filter(
        (x) => x.name === 'Value'
    )[0];

    const OptionsField = formDefinition?.fields?.filter(
        (x) => x.name === 'Options'
    )[0];

    const isNumeric = enableNumericFormatting && formDefinition.isNumeric;

    let options: FormOptionItem[] = [];

    if (OptionsField?.options) {
        options = OptionsField?.options.map((option: FormOptionItem) => {
            const formOption: FormOptionItem = {
                label: t(`${option.label}`),
                value: option.value,
                parentId: option.parentId,
                addDefaultTag: option.addDefaultTag,
            };
            return formOption;
        });

        if (formDefinition.isSortable ?? true) {
            options.sort((a, b) => a.label.localeCompare(b.label));
        }
    }

    if (
        ValueField?.conditionalValidation?.rules ||
        OptionsField?.conditionalValidation?.rules
    ) {
        const conditionalValidations = yup.object().shape({
            inputValue: ValueField?.conditionalValidation?.rules
                ? getRulesSetup(
                      yup.string().nullable(),
                      ValueField?.conditionalValidation?.rules,
                      t
                  )
                : yup.string().nullable(),
            selectedOptions: OptionsField?.conditionalValidation?.rules
                ? getRulesSetup(
                      yup.string().nullable(),
                      OptionsField?.conditionalValidation?.rules,
                      t
                  )
                : yup.string().nullable(),
        });

        if (genericFieldStructure.associatedValidationBasedOnOtherFieldValues) {
            genericFieldStructure.associatedValidationBasedOnOtherFieldValues.rules =
                conditionalValidations;
        }
    }

    const validations =
        ValueField?.rules || OptionsField?.rules
            ? yup.object().shape({
                  inputValue: ValueField?.rules
                      ? getRulesSetup(
                            yup.string().nullable(),
                            ValueField?.rules,
                            t
                        )
                      : yup.string().nullable(),
                  selectedOptions: OptionsField?.rules
                      ? getRulesSetup(
                            yup.string().nullable(),
                            OptionsField?.rules,
                            t
                        )
                      : yup.string().nullable(),
              })
            : undefined;

    const dropdownProps = {
        placeholder: t('form-content:Select'),
        isClearable: true,
        innerPropsOfValueTag: { 'data-test': 'unit' },
    };

    const mappedOptions = options.map((option) => ({
        label: option.label,
        value: option.value as string,
    }));


    // For numeric fields, use a custom render to handle focus/blur formatting
    if (isNumeric) {
        return {
            ...genericFieldStructure,
            type: 'custom',
            validations,
            render: createNumericInputDropdownRender({
                locale,
                options: mappedOptions,
                dropdownSide: 'left',
                dropdownMaxWidth: '50%',
                dropdownProps,
                disabled: genericFieldStructure.disabled,
                ariaRequired: genericFieldStructure.ariaRequired,
                fieldLabel: genericFieldStructure.fieldLabel,
                fieldWrapperProperties: genericFieldStructure.fieldWrapperProperties as Record<string, unknown>,
            }),
        } as CustomFieldStructure;
    }

    // For non-numeric fields, use the standard input-dropdown type
    return {
        ...genericFieldStructure,
        type: 'input-dropdown',
        dropdownSide: 'left',
        dropdownProps,
        dropdownMaxWidth: '50%',
        optionsList: () => mappedOptions,
        validations,
    };
};

export const mapInputWithDropdownReadOnly = (
    content: any,
    translationFileName: string,
    t: TFunction<string, unknown>,
    locale?: string,
    enableNumericFormatting?: boolean
) => {
    const displayLocale = locale ?? i18next.language ?? 'en-GB';
    const formattedValue = enableNumericFormatting
        ? formatNumberForLocale(content.inputValue, displayLocale)
        : content.inputValue;
    return `${formattedValue || content.inputValue} ${
        translationFileName
            ? t(`${translationFileName}:${content.selectedOptions}`)
            : content.selectedOptions
    }`;
};
